import React, { useMemo, useState } from "react";
import { CircularProgress, IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { FaAngleDown, FaAngleUp, FaDownload } from "react-icons/fa";
import { useQuery } from "react-query";
import moment from "moment";
import { getReviewerFeedbackHistory } from "../config/mainRequest";

// Raw string_status codes (api/config/reviewerstring.py).
const STATUS_LABELS = { 0: "Error", 1: "Fixed", 2: "Reviewed", 3: "Ignored" };

const downloadButtonSx = { color: "rgba(232,108,0,1)", borderRadius: "6px", "&:hover": { backgroundColor: "rgba(232,108,0,0.12)" } };

// Past this the history list scrolls inside the popover.
const LIST_MAX_HEIGHT = 360;

const DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

// API datetimes are UTC; shown in the viewer's local time.
const toLocalDatetime = (value) => {
  if (!value) return "";
  const parsed = moment.utc(value, DATETIME_FORMAT, true);
  return parsed.isValid() ? parsed.local().format(DATETIME_FORMAT) : value;
};

const CSV_HEADERS =["String", "Status", "Comment"];

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadCsv = (rows, filename) => {
  const csv = [CSV_HEADERS, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Bar (same look as the video URL bar) that opens the history of every
// review on a match, newest first, each downloadable as CSV.
const ReviewHistoryDropdown = ({ matchId, token, reviewers = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  // Fetched only while the dropdown is open; each reopen refetches (data is stale immediately).
  const { data, isError } = useQuery({
    queryKey: ["getReviewerFeedbackHistory", matchId],
    queryFn: () => getReviewerFeedbackHistory({ matchId, token }),
    enabled: !!anchorEl && !!matchId && !!token,
  });

  // Newest first (same order as the API), sorted on the UTC "YYYY-MM-DD HH:MM:SS"
  // string (sorts correctly as text), then converted to local time.
  const entries = useMemo(
    () =>
      [...(data?.history ?? [])]
        .sort((a, b) => String(b.datetime ?? "").localeCompare(String(a.datetime ?? "")))
        .map((entry) => ({ ...entry, datetime: toLocalDatetime(entry.datetime) })),
    [data]
  );

  const reviewerName = (id, name) =>
    name ||
    reviewers.find((r) => String(r.id) === String(id))?.name ||
    (id != null ? `Reviewer ${id}` : "Unknown reviewer");

  const totalReviews = entries.length;

  const summary = isError
    ? "Unavailable"
    : !data
    ? anchorEl ? "Loading…" : "View"
    : totalReviews === 0
    ? "Not reviewed yet"
    : `${totalReviews} ${totalReviews === 1 ? "review" : "reviews"}`;

  const handleOpen = (e) => setAnchorEl(e.currentTarget);

  // One row per string in the review's string_details.
  const handleDownload = (entry) => {
    const rows = Object.entries(entry.string_details ?? {}).map(([string, details]) => [
      string,
      STATUS_LABELS[details?.string_status] ?? details?.string_status,
      details?.comments,
    ]);
    const stamp = String(entry.datetime ?? "").replace(/[^0-9]+/g, "-");
    downloadCsv(rows, `review_history_${matchId}_${entry.reviewer_id ?? "unknown"}_${stamp}.csv`);
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        onClick={handleOpen}
        sx={{
          height: "100%",
          boxSizing: "border-box",
          px: 2,
          py: 1,
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          borderRadius: "10px",
          cursor: "pointer",
          transition: "border-color 0.15s ease",
          "&:hover": { borderColor: "rgba(232,108,0,0.5)" },
        }}
      >
        <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "var(--text-2)", whiteSpace: "nowrap" }}>Review History</Typography>
        <Stack flex={1} />
        <Typography noWrap sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-1)" }}>{summary}</Typography>
        {anchorEl ? <FaAngleUp color="var(--text-2)" size={12} /> : <FaAngleDown color="var(--text-2)" size={12} />}
      </Stack>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 2,
              width: 440,
              maxWidth: "calc(100vw - 32px)",
              backgroundColor: "var(--surface-1)",
              border: "1px solid var(--border-1)",
              borderRadius: "12px",
            },
          },
        }}
      >
        <Stack gap={1.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "var(--text-1)" }}>Review History</Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "rgba(232,108,0,1)" }}>#{data?.match_id ?? matchId}</Typography>
          </Stack>

          {isError ? (
            <Typography sx={{ fontSize: "12px", color: "#d32f2f" }}>Couldn't load review history.</Typography>
          ) : !data ? (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
            </Stack>
          ) : entries.length === 0 ? (
            <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>No reviews yet.</Typography>
          ) : (
            <Stack sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: "auto", backgroundColor: "var(--surface-2)", borderRadius: "8px" }}>
              {entries.map((entry, index) => (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{ flexShrink: 0, pl: 1.5, pr: 0.75, py: 0.5, borderTop: index ? "1px solid var(--border-1)" : "none" }}
                >
                  <Typography noWrap sx={{ fontSize: "12px", fontWeight: 600, color: "var(--text-1)" }}>
                    {reviewerName(entry.reviewer_id, entry.reviewer_name) + (entry.reviewer_id != null ? ` (#${entry.reviewer_id})` : "")}
                  </Typography>
                  <Stack flex={1} />
                  <Typography sx={{ fontSize: "12px", color: "var(--text-2)", whiteSpace: "nowrap" }}>{entry.datetime}</Typography>
                  <Tooltip title="Download CSV">
                    <IconButton size="small" onClick={() => handleDownload(entry)} sx={downloadButtonSx}>
                      <FaDownload size={12} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default ReviewHistoryDropdown;
